import styled from '@emotion/styled';
import React, { useState } from 'react';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { buildCSSWithLength, getZIndex } from '../../styles';
import { ListItem, ListItemProps } from '../list-item';
import { createPortal } from 'react-dom';
import { restrictToParentElement } from '@dnd-kit/modifiers';

export interface ListProps<T = string> {
  items: Array<ListItemProps<T>>;
  /**
   * 항목을 드래그해서 순서를 바꿀 수 있는 기능 확장 여부
   * @default false
   */
  draggable?: boolean;
  /**
   * 드래그시 오버레이 영역의 z-index 값
   * @default 1065
   */
  dragOverlayZindex?: number;
  /**
   * 드래그될 경우 변경된 items값을 파라미터로 넘겨준다
   * @see draggable
   */
  onChange?: (items: Array<ListItemProps<T>>) => void;
  /** @default 200 */
  width?: number;
}

export const List = <T extends string>({
  items,
  draggable = false,
  dragOverlayZindex,
  onChange,
  width = 200,
}: ListProps<T>) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
  );
  const [activeIndex, setActiveIndex] = useState<number>(-1);

  const handleDragStart = ({ active }: DragStartEvent) => {
    if (!draggable) {
      return;
    }
    setActiveIndex(items.findIndex((item) => item.id === active.id));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const activeIndex = items.findIndex(({ id }) => id === active.id);
    const overIndex = items.findIndex(({ id }) => id === over.id);

    onChange?.(arrayMove(items, activeIndex, overIndex));
    setActiveIndex(-1);
  };

  const handleToggle = (isOn?: boolean, index?: number) => {
    // @ts-expect-error: TS2538
    onChange?.([...items.slice(0, index), { ...items[index], isOn }, ...items.slice(index + 1)]);
  };

  const handlePin = (fixed?: boolean, index?: number) => {
    // @ts-expect-error: TS2538
    onChange?.([...items.slice(0, index), { ...items[index], fixed }, ...items.slice(index + 1)]);
  };

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      modifiers={[restrictToParentElement]}
    >
      <SortableContext items={items.map((item) => item.id)} strategy={verticalListSortingStrategy}>
        <Wrapper width={width} className='pds-list' data-testid='pds-list'>
          {items.map(({ id, text, isOn, fixed, disabled }, index) => (
            <ListItem<T>
              key={id}
              id={id}
              text={text}
              isOn={isOn}
              fixed={fixed}
              disabled={disabled}
              index={index}
              draggable={draggable}
              onToggle={handleToggle}
              onPin={handlePin}
            />
          ))}
          {createPortal(
            // iframe이거나 상위엘리먼트가 overflow 속성을 가지면 hidden되어 createPortal로 body에 렌더링함
            <DragOverlay zIndex={dragOverlayZindex ?? getZIndex('draggable')}>
              {activeIndex > -1 && (
                <ListItem<T>
                  {...items[activeIndex]}
                  draggingOverlay
                  index={activeIndex}
                  draggable={draggable}
                  onToggle={handleToggle}
                  onPin={handlePin}
                />
              )}
            </DragOverlay>,
            document.body,
          )}
        </Wrapper>
      </SortableContext>
    </DndContext>
  );
};

const Wrapper = styled.div<{ width: number }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  ${({ width }) => buildCSSWithLength('width', width)};
`;
